
export interface TelegramThemeParams {
  bg_color?: string
  text_color?: string
  hint_color?: string
  link_color?: string
  button_color?: string
  button_text_color?: string
  secondary_bg_color?: string
  header_bg_color?: string
  bottom_bar_bg_color?: string
  accent_text_color?: string
  section_bg_color?: string
  section_header_text_color?: string
  section_separator_color?: string
  subtitle_text_color?: string
  destructive_text_color?: string
}

interface TelegramInsets {
  top: number
  bottom: number
  left: number
  right: number
}

interface TelegramBackButton {
  isVisible: boolean
  show(): void
  hide(): void
  onClick(cb: () => void): void
  offClick(cb: () => void): void
}

/** 6.1+: тактильный отклик. */
interface TelegramHapticFeedback {
  impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void
  notificationOccurred(type: 'error' | 'success' | 'warning'): void
  selectionChanged(): void
}

export interface DownloadFileParams {
  /** Абсолютная https-ссылка; сервер отдаёт файл с Content-Disposition: attachment. */
  url: string
  file_name: string
}

type TelegramEvent =
  | 'themeChanged'
  | 'viewportChanged'
  | 'safeAreaChanged'
  | 'contentSafeAreaChanged'
  | 'fileDownloadRequested'

export interface TelegramWebApp {
  initData: string
  initDataUnsafe: { start_param?: string; user?: { id: number } }
  version: string
  platform: string
  colorScheme: 'light' | 'dark'
  themeParams: TelegramThemeParams
  isExpanded: boolean
  viewportHeight: number
  viewportStableHeight: number
  safeAreaInset?: TelegramInsets
  contentSafeAreaInset?: TelegramInsets
  BackButton: TelegramBackButton
  HapticFeedback?: TelegramHapticFeedback
  ready(): void
  expand(): void
  close(): void
  isVersionAtLeast(version: string): boolean
  disableVerticalSwipes?(): void
  setHeaderColor?(color: string): void
  setBackgroundColor?(color: string): void
  setBottomBarColor?(color: string): void
  /** 6.2+: спросить «Закрыть?», пока идёт загрузка. */
  enableClosingConfirmation?(): void
  disableClosingConfirmation?(): void
  /** 8.0+: родное окно «Скачать файл». */
  downloadFile?(params: DownloadFileParams, callback?: (accepted: boolean) => void): void
  /** 9.1+: закрыть экранную клавиатуру WebView. */
  hideKeyboard?(): void
  openLink(url: string): void
  openTelegramLink(url: string): void
  onEvent(event: TelegramEvent, cb: (payload?: unknown) => void): void
  offEvent(event: TelegramEvent, cb: (payload?: unknown) => void): void
}

export interface TelegramWidgetUser {
  id: number
  first_name: string
  last_name?: string
  username?: string
  photo_url?: string
  auth_date: number
  hash: string
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp }
    onTelegramAuth?: (user: TelegramWidgetUser) => void
  }
}

export function getWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null
}

/** SDK есть и вне Telegram, но initData непустой только внутри Mini App. */
export function isInTelegram(): boolean {
  return Boolean(getWebApp()?.initData)
}

/**
 * Mini App и клиент Telegram не ниже версии API. Что с какой версии:
 * 6.1 — BackButton и цвет шапки, 6.2 — подтверждение закрытия,
 * 6.9 — шапка любым цветом, 7.10 — цвет нижней панели, 8.0 — downloadFile,
 * 9.1 — hideKeyboard.
 */
export function tgVersionAtLeast(version: string): boolean {
  const tg = getWebApp()
  if (!tg || !isInTelegram()) return false
  try {
    return tg.isVersionAtLeast(version)
  } catch {
    return false
  }
}

/** Системная «Назад» Telegram есть: свои стрелки «Назад» не нужны. */
export function hasTelegramBack(): boolean {
  return tgVersionAtLeast('6.1')
}

export function applyTelegramTheme(): void {
  const tg = getWebApp()
  if (!tg || !isInTelegram()) return
  const root = document.documentElement
  root.dataset.tg = ''
  root.dataset.theme = tg.colorScheme
  const background = getComputedStyle(root).getPropertyValue('--bg').trim()
  document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => {
    meta.setAttribute('content', background)
  })
  try {
    if (tgVersionAtLeast('6.9')) tg.setHeaderColor?.(background)
    else if (tgVersionAtLeast('6.1')) tg.setHeaderColor?.('bg_color')
    if (tgVersionAtLeast('6.1')) tg.setBackgroundColor?.(tgVersionAtLeast('6.9') ? background : 'bg_color')
    if (tgVersionAtLeast('7.10')) tg.setBottomBarColor?.(background)
  } catch {
    // старые клиенты Telegram не знают части методов
  }
}

/** Вызывается один раз до рендера. */
export function initTelegram(): void {
  const tg = getWebApp()
  if (!tg || !isInTelegram()) return
  applyTelegramTheme()
  tg.onEvent('themeChanged', applyTelegramTheme)
  try {
    tg.ready()
    tg.expand()
    if (tgVersionAtLeast('7.7')) tg.disableVerticalSwipes?.()
  } catch {
    // старые клиенты Telegram не знают части методов
  }
}

/**
 * Тактильный отклик Telegram (6.1+): захват и перенос на плане. Вне Telegram
 * и в старых клиентах — ничего.
 */
function withHaptic(fn: (h: TelegramHapticFeedback) => void): void {
  const h = getWebApp()?.HapticFeedback
  if (!h || !tgVersionAtLeast('6.1')) return
  try {
    fn(h)
  } catch {
    // старый клиент
  }
}

export const haptic = {
  impact: (style: 'light' | 'medium' = 'medium') => withHaptic((h) => h.impactOccurred(style)),
  selection: () => withHaptic((h) => h.selectionChanged()),
  notify: (type: 'success' | 'error') => withHaptic((h) => h.notificationOccurred(type)),
}

// Причины спросить «Закрыть?»: загрузка уникализации, несохранённая карточка плана.
// Подтверждение снимается, только когда не осталось ни одной.
const closingReasons = new Set<string>()

/** Пока идёт загрузка, Telegram спросит «Закрыть?» (6.2+). `reason` — чья это просьба. */
export function setClosingConfirmation(on: boolean, reason = 'upload'): void {
  if (on) closingReasons.add(reason)
  else closingReasons.delete(reason)
  const tg = getWebApp()
  if (!tg || !tgVersionAtLeast('6.2')) return
  try {
    if (closingReasons.size > 0) tg.enableClosingConfirmation?.()
    else tg.disableClosingConfirmation?.()
  } catch {
    // старый клиент
  }
}
